import {
  type LocationQuery,
  LocationQueryEnum,
  type LocationResponse,
} from "@b2bportal/air-shopping-api";
import { fetchLocationAutocomplete, logger } from "@hopper-b2b/api";
import { type ITripTerminus, TripCategory } from "@hopper-b2b/types";
import { putResolve, select } from "redux-saga/effects";
import {
  type IStoreState,
  flightApiConfigStoreKey,
} from "../../../../reducers/types";
import { PATH_HOME } from "../../../../utils/urlPaths";
import { actions as searchActions } from "../../../search/actions";
import { setMobileSearchProgress } from "../../../search/actions/actions";
import { MobileFlightSearchStep } from "../../../search/reducer";
import type { actions } from "../../actions";
import { coerceStopsOption } from "../../utils/inferTripCategoryAndStops";
import type { IFlightShopParsedQuery } from "../../utils/parseQueryString";
import { buildFlightShopQueryDiagnostics } from "../../utils/priceWatchDiagnostics";
import { resolveFlightLocation } from "../../utils/resolveFlightLocation";
import {
  getExistingStateVariables,
  populateFlightShopQueryParametersFromState,
} from "../populateShopQueryParamsSaga";

type FlightShopRequiredParams = {
  origin?: ITripTerminus | null;
  destination?: ITripTerminus | null;
  departureDate?: Date | null;
  tripCategory?: TripCategory;
  returnDate?: Date | null;
  stopsOption?: unknown;
};

export const getMissingFlightShopParams = ({
  origin,
  destination,
  departureDate,
  tripCategory,
  returnDate,
  stopsOption,
}: FlightShopRequiredParams) => {
  const missingParams: string[] = [];
  if (!origin) missingParams.push("origin");
  if (!destination) missingParams.push("destination");
  if (!departureDate) missingParams.push("departureDate");
  if (!stopsOption) missingParams.push("stopsOption");
  if (tripCategory === TripCategory.ROUND_TRIP && !returnDate) {
    missingParams.push("returnDate");
  }
  return missingParams;
};

export function* setUpFlightShopParams(action: actions.IFetchTripSummaries) {
  const state: IStoreState = yield select();
  const apiConfig = state[flightApiConfigStoreKey];
  const history = action.history;

  let {
    departureDate,
    returnDate,
    origin,
    destination,
    tripCategory,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
    stopsOption,
    noLCC,
  } = getExistingStateVariables(state);

  const parsedQueryString: IFlightShopParsedQuery =
    yield populateFlightShopQueryParametersFromState({ state, history });

  const parsedQueryDiagnostics =
    buildFlightShopQueryDiagnostics(parsedQueryString);

  // Push deeplinks omit `isFromFlightWatch`, so classify by shape instead of
  // the flag. Organic searches are skipped to keep log volume bounded.
  if (parsedQueryDiagnostics.entrySource !== "organic") {
    logger()?.info("[FlightShopHydration] Shop query entry", {
      event: "flight_shop_price_watch_entry",
      ...parsedQueryDiagnostics,
    });
  }

  if (
    !origin ||
    !destination ||
    !departureDate ||
    (tripCategory === TripCategory.ROUND_TRIP && !returnDate) ||
    // note: when it's from FlightWatch, it should treat parsedQueryString as the source of truth.
    parsedQueryString.isFromFlightWatch
  ) {
    logger()?.info("[FlightShopHydration] Starting parameter hydration", {
      event: "flight_shop_parameter_hydration_started",
      ...parsedQueryDiagnostics,
    });

    let correspondingDestination: ITripTerminus | undefined;
    let correspondingOrigin: ITripTerminus | undefined;
    try {
      const correspondingLocations: {
        correspondingDestination: ITripTerminus | undefined;
        correspondingOrigin: ITripTerminus | undefined;
      } = yield fetchOriginDestination(
        parsedQueryString.origin,
        parsedQueryString.destination,
        parsedQueryString.originType,
        parsedQueryString.destinationType
      );
      correspondingDestination =
        correspondingLocations.correspondingDestination;
      correspondingOrigin = correspondingLocations.correspondingOrigin;
    } catch (error) {
      logger()?.warn("[FlightShopHydration] Location lookup failed", {
        event: "flight_shop_location_lookup_failed",
        ...parsedQueryDiagnostics,
        errorType: error instanceof Error ? error.name : typeof error,
      });
      throw error;
    }

    [
      origin,
      destination,
      departureDate,
      returnDate,
      tripCategory,
      stopsOption,
      noLCC,
    ] = [
      correspondingOrigin,
      correspondingDestination,
      parsedQueryString.departureDate,
      parsedQueryString.returnDate,
      parsedQueryString.tripCategory ||
        (parsedQueryString.returnDate
          ? TripCategory.ROUND_TRIP
          : TripCategory.ONE_WAY),
      coerceStopsOption(parsedQueryString.stopsOption),
      parsedQueryString.noLCC,
    ];

    const missingRequiredParams = getMissingFlightShopParams({
      departureDate,
      origin,
      destination,
      returnDate,
      tripCategory,
      stopsOption,
    });

    // If we are missing the data we need from both the state and the query params
    // we should redirect the user home.
    if (missingRequiredParams.length > 0) {
      logger()?.warn("[FlightShopHydration] Parameter hydration failed", {
        event: "flight_shop_parameter_hydration_failed",
        ...parsedQueryDiagnostics,
        resolvedOriginCode: correspondingOrigin?.id.code.code ?? null,
        resolvedDestinationCode: correspondingDestination?.id.code.code ?? null,
        missingRequiredParams: missingRequiredParams.join(","),
      });
      yield putResolve(
        setMobileSearchProgress(MobileFlightSearchStep.LocationSearch)
      );
      action.history.push(PATH_HOME);
      return;
    }

    if (
      parsedQueryString.isFromFlightWatch ||
      parsedQueryDiagnostics.entrySource === "deeplink_without_watch_flag"
    ) {
      logger()?.info("[FlightShopHydration] Parameter hydration succeeded", {
        event: "flight_shop_parameter_hydration_succeeded",
        ...parsedQueryDiagnostics,
        resolvedOriginCode: correspondingOrigin?.id.code.code ?? null,
        resolvedDestinationCode: correspondingDestination?.id.code.code ?? null,
      });
    }

    // Order here matters because of the reducer structure.
    // Set trip category resets the returnDate field.
    yield putResolve(searchActions.setTripCategory(tripCategory));
    yield putResolve(searchActions.setOrigin(origin));
    yield putResolve(searchActions.setDestination(destination));
    yield putResolve(searchActions.setDepartureDate(departureDate));
    yield putResolve(searchActions.setReturnDate(returnDate));
    yield putResolve(searchActions.setStopsOption(stopsOption, apiConfig));
    yield putResolve(
      searchActions.setFareclassOptionFilter({
        basic: false,
        standard: noLCC,
        enhanced: noLCC,
        premium: noLCC,
        luxury: noLCC,
      })
    );
  }

  return {
    departureDate,
    returnDate,
    origin,
    destination,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
    stopsOption,
    noLCC,
  };
}

export function* fetchOriginDestination(
  origin: string,
  destination: string,
  originType?: string,
  destinationType?: string
) {
  const originRequestBody: LocationQuery = {
    LocationQuery: LocationQueryEnum.Label,
    l: origin,
  };
  const state: IStoreState = yield select();
  const apiConfig = state[flightApiConfigStoreKey];
  const { categories: originCategories }: LocationResponse =
    yield fetchLocationAutocomplete(originRequestBody, apiConfig);
  const correspondingOrigin = resolveFlightLocation(
    { categories: originCategories },
    origin,
    originType
  );

  const destinationRequestBody: LocationQuery = {
    LocationQuery: LocationQueryEnum.Label,
    l: destination,
  };

  const { categories: destinationCategories }: LocationResponse =
    yield fetchLocationAutocomplete(destinationRequestBody, apiConfig);
  const correspondingDestination = resolveFlightLocation(
    { categories: destinationCategories },
    destination,
    destinationType
  );

  return { correspondingDestination, correspondingOrigin };
}
